import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import fetchService from "../utils/fetch.service";
import { MainContext } from "../context/MainContext";
import Loader from "../components/Loader";

const Page = styled.section`
    .back-link {
        min-height: 44px;
        display: inline-flex;
        align-items: center;
        color: var(--text-soft);
        font-size: 14px;
        font-weight: 650;
    }

    h1 {
        margin: 10px 0 8px;
        font-family: Georgia, "Times New Roman", serif;
        font-size: clamp(36px, 10vw, 50px);
        line-height: 1;
        letter-spacing: -0.05em;
    }

    > p { margin: 0 0 24px; color: var(--muted); }
`;

const UserList = styled.div`
    border-top: 1px solid var(--line-strong);
`;

const UserRow = styled.article`
    padding: 18px 0;
    display: grid;
    grid-template-columns: 48px minmax(0, 1fr);
    gap: 14px;
    border-bottom: 1px solid var(--line);

    img,
    .avatar {
        width: 48px;
        height: 48px;
        display: grid;
        place-items: center;
        border-radius: 50%;
        background: #76506c;
        color: var(--text);
        object-fit: cover;
        font-weight: 750;
    }

    h2 { margin: 0; font-size: 16px; }
    .username { margin: 2px 0 10px; color: var(--muted); font-size: 13px; }

    dl {
        margin: 0;
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px 12px;
    }

    dt { color: var(--muted); font-size: 11px; text-transform: uppercase; }
    dd { margin: 1px 0 0; color: var(--text-soft); font-size: 13px; }

    @media (min-width: 760px) {
        grid-template-columns: 52px 230px minmax(0, 1fr);
        align-items: center;
        img, .avatar { width: 52px; height: 52px; }
        .username { margin-bottom: 0; }
        dl { grid-template-columns: repeat(4, minmax(0, 1fr)); }
    }
`;

const State = styled.p`
    padding: 24px 0;
    color: var(--text-soft) !important;
`;

const initials = (name) => (name || "?").slice(0, 2).toUpperCase();
const bool = (value) => value ? "Yes" : "No";

const AdminPage = () => {
    const [profiles, setProfiles] = useState([]);
    const [status, setStatus] = useState("loading");
    const { userContext } = useContext(MainContext);
    const [user] = userContext;

    useEffect(() => {
        fetchService.post("/user-all").then((res) => {
            if (!Array.isArray(res.data)) {
                setStatus("error");
                return;
            }
            setProfiles(res.data);
            setStatus("ready");
        }).catch(() => setStatus("error"));
    }, []);

    if (user.role !== "admin" && user.role !== "moderator") {
        return <State role="alert">You do not have access to this page.</State>;
    }

    return (
        <Page>
            <Link className="back-link" to="/">← Back to search</Link>
            <h1>Users</h1>
            <p>Review profiles and account status.</p>

            { status === "loading" && <Loader/> }
            { status === "error" && <State role="alert">Users could not be loaded. Please try again.</State> }
            { status === "ready" && profiles.length === 0 && <State>No users found.</State> }
            { status === "ready" && profiles.length > 0 && (
                <UserList>
                    { profiles.map((profile) => (
                        <UserRow key={ profile._id }>
                            { profile.photo_url
                                ? <img src={ profile.photo_url } alt=""/>
                                : <div className="avatar" aria-hidden="true">{ initials(profile.displayName) }</div> }
                            <div>
                                <h2>{ profile.displayName || "No display name" }</h2>
                                <p className="username">@{ profile.username || "no_username" }</p>
                            </div>
                            <dl>
                                <div><dt>Age</dt><dd>{ profile.age || "—" }</dd></div>
                                <div><dt>Gender</dt><dd>{ profile.gender || "—" }</dd></div>
                                <div><dt>Orientation</dt><dd>{ profile.orientation || "—" }</dd></div>
                                <div><dt>Verified</dt><dd>{ bool(profile.po) }</dd></div>
                                <div><dt>Visible</dt><dd>{ bool(profile.displayOnSearch) }</dd></div>
                                <div><dt>Enabled</dt><dd>{ bool(profile.enabled) }</dd></div>
                                <div><dt>Role</dt><dd>{ profile.role || "user" }</dd></div>
                            </dl>
                        </UserRow>
                    )) }
                </UserList>
            ) }
        </Page>
    );
};

export default AdminPage;
